// ── Types ─────────────────────────────────────────────────────────────────
export type GradeLevel = "K-2" | "3-5" | "6-8" | "9-12";
// Empty string means "leader has not yet provided a grade for this
// incident". Using "" (not the K-2/etc enum values) avoids the
// historical bug where "6-8" was overloaded as both a real grade
// AND a sentinel for "not set", which made every new draft pretend
// to be a Grade 6-8 incident.
export type GradeLevelOrEmpty = GradeLevel | "";

// Default grade band used ONLY when a downstream lookup needs a
// concrete GradeLevel (e.g. indexing into GRADE_GUIDANCE) and the
// leader has not yet entered one. Never persisted as the stored
// value — see DEFAULT_INCIDENT.grade_level which stays empty.
export const GRADE_FALLBACK: GradeLevel = "6-8";

export const isGradeSet = (
  g: GradeLevelOrEmpty | null | undefined,
): g is GradeLevel => !!g;

// Use at code sites that index into a Record<GradeLevel, ...> map
// (GRADE_GUIDANCE, STUDENT_REFLECTION_PROMPTS, etc.) so the lookup
// stays defined when no grade has been captured yet.
export const effectiveGrade = (
  g: GradeLevelOrEmpty | null | undefined,
): GradeLevel => (isGradeSet(g) ? g : GRADE_FALLBACK);

// Use at user-facing render sites (PDF/HTML/text) where we'd
// otherwise print "Grade " with nothing after it, or worse
// "Grade 6-8" before the leader picked anything.
export const formatGradeForDoc = (
  g: GradeLevelOrEmpty | null | undefined,
): string => (isGradeSet(g) ? g : "Not specified");
export type SafetyCheck = "safe_now" | "pause_needed";
export type SafetyCheckOrEmpty = SafetyCheck | "";
export type ChosenPath = "one_to_one_conversation" | "supported_meeting" | "pause_support" | "documentation_only";
export type ChosenPathOrEmpty = ChosenPath | "";
export type IncidentStatus = "intake" | "coaching" | "followup" | "closed" | "paused";

// Display helpers — keep PDF/HTML output honest about what the leader
// has actually assessed vs. what's still default.
export const formatSafetyCheckLabel = (
  s: SafetyCheckOrEmpty | null | undefined,
): { html: string; text: string } => {
  if (s === "pause_needed") {
    return {
      html: "⚠ Pause needed — prioritize support and stability before any repair conversation.",
      text: "⚠ Pause needed",
    };
  }
  if (s === "safe_now") {
    return {
      html: "✓ Safe to proceed with a repair-centered conversation.",
      text: "✓ Safe to proceed",
    };
  }
  return {
    html: "Not yet assessed.",
    text: "Not yet assessed",
  };
};

export const formatChosenPathLabel = (
  p: ChosenPathOrEmpty | null | undefined,
): string => {
  switch (p) {
    case "one_to_one_conversation":
      return "1:1 conversation";
    case "supported_meeting":
      return "Supported meeting";
    case "pause_support":
      return "Pause / regulate first";
    case "documentation_only":
      return "Documentation only";
    default:
      return "Not yet chosen";
  }
};

export type ShareScope = "private" | "school";

export type WitnessAccount = {
  id: string;
  witness_name: string;
  witness_role: string;
  account: string;
};

export type Incident = {
  id: string;
  created_at: string;
  updated_at: string;
  user_id: string | null;
  // Populated only for school incidents (joined from profiles)
  creator_name?: string;
  share_scope: ShareScope;
  status: IncidentStatus;

  // Stage 1 — Intake
  student_identifier: string;
  grade_level: GradeLevelOrEmpty;
  teacher_name: string;
  incident_description: string;
  repeat_incident: boolean;
  student_reflection_responses: string;
  reflection_received: boolean;

  // Stage 1 — Referring narrative
  referring_teacher_notes: string;
  prior_interventions: string;
  prior_interventions_checked: string[];
  teacher_requested_outcome: string;
  teacher_outcome_checked: string[];

  // Stage 2 — Coaching
  // Both default to empty so a fresh draft does NOT advertise a fake
  // "✓ Safe to proceed / Path: 1:1 conversation" before the leader
  // has run the safety check or chosen a path. Helpers above
  // (formatSafetyCheckLabel / formatChosenPathLabel) translate the
  // empty case into honest copy at every render site.
  safety_check: SafetyCheckOrEmpty;
  chosen_path: ChosenPathOrEmpty;
  conversation_notes: string;
  conversation_completed: boolean;

  // Stage 2 — Due process
  due_process_acknowledged: boolean;
  due_process_timestamp: string;
  due_process_student_name: string;
  due_process_admin_initials: string;

  // Stage 3 — Follow-up
  iss_reflection_completed: boolean;
  parent_contact_type: "" | "student_with_adult" | "adult_alone";
  parent_call_notes: string;
  behavior_follow_up: string;
  follow_up_date: string;

  // Stage 3 — Evidence / witness accounts
  witness_accounts: WitnessAccount[];

  // Stage 4 — Removal type & rationale
  removal_type: "administrative_redirect" | "formal_removal" | "";
  removal_rationale: string;
  iep_504_flag: boolean;

  // Stage 4 — ISS academic credit plan
  iss_work_plan: string;

  // Stage 4 — Return to class plan
  return_to_class_plan: string;
  reentry_conversation_needed: boolean;
  reentry_facilitator: string;
  reentry_checkin_schedule: string;
};

export const DEFAULT_INCIDENT: Omit<Incident, "id" | "created_at" | "updated_at" | "user_id"> = {
  share_scope: "school",
  status: "intake",
  student_identifier: "",
  grade_level: "",
  teacher_name: "",
  incident_description: "",
  repeat_incident: false,
  student_reflection_responses: "",
  reflection_received: false,
  referring_teacher_notes: "",
  prior_interventions: "",
  prior_interventions_checked: [],
  teacher_requested_outcome: "",
  teacher_outcome_checked: [],
  safety_check: "",
  chosen_path: "",
  conversation_notes: "",
  conversation_completed: false,
  due_process_acknowledged: false,
  due_process_timestamp: "",
  due_process_student_name: "",
  due_process_admin_initials: "",
  iss_reflection_completed: false,
  parent_contact_type: "",
  parent_call_notes: "",
  behavior_follow_up: "",
  follow_up_date: "",
  witness_accounts: [],
  removal_type: "",
  removal_rationale: "",
  iep_504_flag: false,
  iss_work_plan: "",
  return_to_class_plan: "",
  reentry_conversation_needed: false,
  reentry_facilitator: "",
  reentry_checkin_schedule: "",
};
